import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from "react";
import { useMutation } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { useFeedbackTracker } from "@/hooks/use-feedback-tracker";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";
import {
  SmilePlus,
  Smile,
  Meh,
  Frown,
  Frown as FrownSad,
} from "lucide-react";

// Rating component
interface CSATRatingProps {
  value: number;
  onChange: (value: number) => void;
}

// CSAT rating component with emoji faces
const CSATRating: React.FC<CSATRatingProps> = ({ value, onChange }) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  
  // Define the emoji faces for each rating level
  const emojis = [
    { value: 1, icon: FrownSad, label: t("Very Dissatisfied") },
    { value: 2, icon: Frown, label: t("Dissatisfied") },
    { value: 3, icon: Meh, label: t("Neutral") },
    { value: 4, icon: Smile, label: t("Satisfied") },
    { value: 5, icon: SmilePlus, label: t("Very Satisfied") },
  ];
  
  // For RTL languages like Arabic, reverse the order
  const displayEmojis = isRTL ? [...emojis].reverse() : emojis;
  
  return (
    <div className="flex flex-col items-center space-y-2">
      <Label className="text-center mb-2">{t("How would you rate your experience?")}</Label>
      <div className={cn(
        "flex items-center justify-center",
        isRTL ? "space-x-reverse space-x-4" : "space-x-4"
      )}>
        {displayEmojis.map((emoji) => {
          const Icon = emoji.icon;
          return (
            <button
              key={emoji.value}
              type="button"
              className={cn(
                "flex flex-col items-center p-2 rounded-full transition-all",
                value === emoji.value
                  ? "text-primary scale-110 bg-primary/10 ring-2 ring-primary"
                  : "text-muted-foreground hover:text-primary hover:bg-primary/5"
              )}
              onClick={() => onChange(emoji.value)}
              aria-label={emoji.label}
            >
              <Icon className="h-8 w-8 mb-1" />
              <span className="text-xs">{emoji.value}</span>
            </button>
          );
        })}
      </div>
      <div className="text-xs text-muted-foreground text-center mt-1">
        {emojis.find(emoji => emoji.value === value)?.label || t("Select a rating")}
      </div>
    </div>
  );
};

interface FeedbackFormData {
  csatRating: number;
  comment: string;
  contactNumber?: string;
}

interface FeedbackResponse {
  id: number;
  csatRating: number;
  comment: string | null;
  contactNumber: string | null;
  feedbackTypeId: number;
  successMessage: string;
}

interface CSATFeedbackProps {
  /** Optional custom trigger element */
  children?: React.ReactNode;
  /** Optional className for the trigger */
  className?: string;
  /** Optional variant for the button */
  buttonVariant?: "outline" | "ghost" | "link" | "default" | "icon";
  /** Optional size for the button */
  buttonSize?: "sm" | "default" | "lg";
}

export interface CSATFeedbackHandle {
  openDialog: () => void;
}

// Create a forwardRef component to handle the ref properly
const CSATFeedback = forwardRef<CSATFeedbackHandle, CSATFeedbackProps>(({ 
  children, 
  className,
  buttonVariant = "outline",
  buttonSize = "default" 
}, ref) => {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);
  const [formData, setFormData] = useState<FeedbackFormData>({
    csatRating: 0,
    comment: "",
    contactNumber: ""
  });
  
  // Expose the openDialog method through the ref
  useImperativeHandle(ref, () => ({
    openDialog: () => setIsOpen(true)
  }));

  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [feedbackType, setFeedbackType] = useState<'General' | 'Bug' | 'Idea'>('General');

  // Import feedback tracker functions
  const { markFeedbackAsSubmitted } = useFeedbackTracker();

  // Mutation for submitting feedback
  const submitFeedback = useMutation<FeedbackResponse, Error, FeedbackFormData>({
    mutationFn: async (data: FeedbackFormData) => {
      const res = await apiRequest("POST", "/api/feedback", data);
      return res.json();
    },
    onSuccess: (response) => {
      // Use the AI-determined custom success message from the server
      setSuccessMessage(response.successMessage || t("Thank you for your feedback!"));
      
      // Determine feedback type based on response
      if (response.feedbackTypeId === 2) {
        setFeedbackType('Bug');
      } else if (response.feedbackTypeId === 3) {
        setFeedbackType('Idea');
      } else {
        setFeedbackType('General');
      }
      
      // Mark feedback as submitted for the FeedbackTracker
      markFeedbackAsSubmitted();
      
      setShowSuccessScreen(true);
      
      // Reset form (but don't close the dialog)
      setFormData({
        csatRating: 0,
        comment: "",
        contactNumber: ""
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("Error"),
        description: error.message || t("Failed to submit feedback. Please try again."),
        variant: "destructive",
      });
    },
  });
  
  // Function to close the dialog and reset state
  const handleClose = () => {
    setIsOpen(false);
    // When closing dialog, reset the success state
    setTimeout(() => {
      setShowSuccessScreen(false);
      setSuccessMessage(null);
    }, 300); // Small delay to allow dialog close animation to complete
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (formData.csatRating === 0) {
      toast({
        title: t("Rating Required"),
        description: t("Please select a rating before submitting."),
        variant: "destructive",
      });
      return;
    }
    submitFeedback.mutate(formData);
  };

  // Prepare the trigger button based on props
  const renderTrigger = () => {
    if (children) {
      return <div className={className}>{children}</div>;
    }
    
    if (buttonVariant === "icon") {
      return (
        <Button 
          variant="outline" 
          size="icon"
          className={cn(
            "relative w-10 h-10 overflow-hidden bg-primary/10 hover:bg-primary/20 border border-primary/20 hover:border-primary/30 shadow-sm hover:shadow-md",
            className
          )}
          aria-label={t("Give Feedback")}
        >
          <span className="text-base">✏️</span>
        </Button>
      );
    }
    
    return (
      <Button 
        variant={buttonVariant} 
        size={buttonSize}
        className={cn(
          "text-sm text-muted-foreground hover:text-primary transition-colors",
          className
        )}
        aria-label={t("Give Feedback")}
      >
        {t("Give Feedback")}
      </Button>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        {renderTrigger()}
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px] w-full h-[100dvh] md:h-auto max-h-full md:max-h-[85vh] rounded-none md:rounded-md p-4 md:p-6 overflow-y-auto flex flex-col">
        <DialogHeader className="text-center">
          <DialogTitle className="text-center">
            {showSuccessScreen ? t("Feedback Submitted") : t("We Value Your Feedback")}
          </DialogTitle>
        </DialogHeader>
        
        {showSuccessScreen ? (
          // Success screen with dynamic icons based on feedback type
          <div className="flex flex-col items-center justify-between flex-1 py-8 my-auto h-full">
            <div className="flex-grow"></div>
            <div className="flex flex-col items-center justify-center">
              <div className="text-7xl mb-6">
                {feedbackType === 'Bug' && '🐛'}
                {feedbackType === 'Idea' && '💡'}
                {feedbackType === 'General' && '✅'}
              </div>
              <p className="text-center text-lg font-medium">
                {/* Correctly translate feedback messages based on feedback type */}
                {feedbackType === 'Bug' && t("Thank you for your bug report!")}
                {feedbackType === 'Idea' && t("Thank you for your idea!")}
                {feedbackType === 'General' && t("Thank you for your rating!")}
              </p>
            </div>
            <div className="flex-grow"></div>
            <Button 
              onClick={handleClose} 
              className="mt-8 min-w-[120px] w-full md:w-auto"
            >
              {t("Close")}
            </Button>
          </div>
        ) : (
          // Feedback form
          <form onSubmit={handleSubmit} className="space-y-6 py-4">
            <CSATRating
              value={formData.csatRating}
              onChange={(value) => setFormData({ ...formData, csatRating: value })}
            />
            
            {/* Feedback type selection removed - now determined automatically by AI */}
            
            <div className="space-y-2">
              <Label htmlFor="comment" className="flex items-center gap-1">
                {t("Comments or Bug Report")}
                <span className="text-xs text-muted-foreground">{t("(Optional)")}</span>
              </Label>
              <Textarea
                id="comment"
                placeholder={t("Please share your thoughts or report any issues...")}
                className="min-h-[100px]"
                value={formData.comment}
                onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contact" className="flex items-center gap-1">
                {t("Contact Number")}
                <span className="text-xs text-muted-foreground">{t("(Optional)")}</span>
              </Label>
              <Input
                id="contact"
                placeholder="+966 xxxxxxx"
                dir="ltr"
                className="text-left"
                value={formData.contactNumber}
                onChange={(e) => setFormData({ ...formData, contactNumber: e.target.value })}
              />
            </div>
            
            <DialogFooter 
              dir={i18n.dir()} 
              className="flex flex-col items-center justify-center gap-2 w-full"
            >
              <Button 
                type="submit" 
                disabled={submitFeedback.isPending}
                className="w-full"
              >
                {submitFeedback.isPending ? t("Submitting...") : t("Submit Feedback")}
              </Button>
              <DialogClose asChild>
                <Button 
                  type="button" 
                  variant="outline"
                  className="w-full"
                >
                  {t("Cancel")}
                </Button>
              </DialogClose>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
});

CSATFeedback.displayName = 'CSATFeedback';

export default CSATFeedback;